import axios from "axios";
import getErrorCodes from "./ErrorCodesService";
import Auth from "./auth/AuthorizationService";

// This will now be used dynamically inside the interceptor
const checkHeaders = () => {
  const token = localStorage.getItem("id_token");
  if (token !== null && token !== undefined) {
    return Auth.headerObjectWithAuthorization();
  } else {
    return {
      "Content-Type": "application/json",
    };
  }
};

const api = axios.create({
  timeout: 30000,
  transformResponse: [...axios.defaults.transformResponse, (data) => data],
  transformRequest: [(data) => data, ...axios.defaults.transformRequest],
});

export const setToken = (token) => {
  api.interceptors.request.use((c) => {
    const configuration = c;
    configuration.headers = {
      ...configuration.headers,
      Authorization: `Bearer ${token}`,
    };
    return configuration;
  });
};

export const getRequestForDownload = (url) => {
  return axios({
    method: "get",
    url: `${url}`,
    headers: Auth.headerObjectWithAuthorization(),
    responseType: "blob",
  });
};

export const postRequestForExcel = (url, data) => {
  return axios({
    method: "post",
    timeout: 30000,
    url: `${url}`,
    data: JSON.stringify(data),
    headers: Auth.headerObjectWithAuthorization(),
    responseType: "arraybuffer",
  });
};

const triggerEvent = (event, payload) => {
  window.dispatchEvent(new CustomEvent(event, payload));
};

// CORRECTED: Dynamically inject headers for every request
api.interceptors.request.use(
  (request) => {
    request.headers = {
      ...request.headers,
      ...checkHeaders(),
    };

    if (!Auth.loggedIn()) {
      triggerEvent("API_TOKEN_EXPIRE_EVENT", {
        detail: { status: "authTokenExpire", request },
      });
    }
    return request;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const transformErrorResponse = (error) => {
  const errorCodeTranslationKeyMapping = getErrorCodes();
  const errorCodes = [];
  const codeMapping = {};
  const codeFieldMapping = {};
  const codeMessageMapping = {};
  const codeTransKeyMapping = {};
  let isMultipleErrors = false;
  let errorCode = "";
  let errorMessageKey = "";

  if (error && error?.errors?.length > 0) {
    let errorArr = error.errors;
    let tempObj = {};
    errorArr.forEach((obj) => {
      tempObj = { ...obj };
      errorCodes.push(obj.code);
      codeMapping[obj.code] = obj;
      codeFieldMapping[obj.code] = obj.field;
      codeMessageMapping[obj.code] = obj.message;
      codeTransKeyMapping[obj.code] = errorCodeTranslationKeyMapping[obj.code];
    });
    isMultipleErrors = error.errors.length > 1;
    if (!isMultipleErrors) {
      errorCode = tempObj.code;
      errorMessageKey = errorCodeTranslationKeyMapping[tempObj.code];
    }
  } else {
    isMultipleErrors = false;
  }

  return {
    ...error,
    errorCode,
    errorCodes,
    errorMessageKey,
    codeMapping,
    codeFieldMapping,
    codeMessageMapping,
    codeTransKeyMapping,
    isMultipleErrors,
  };
};

const apiErrorHandler = (errorObj) => {
  if (!errorObj.response) {
    return Promise.reject(errorObj);
  }

  const errorData = errorObj.response.data;
  const transformedErrorObject = transformErrorResponse(errorData);
  errorObj.response.data = { ...transformedErrorObject };

  const status = errorObj.response.status; // Using HTTP status directly for reliability
  const error = errorObj.response.data;

  // Specific check for KB403001 or 403 status to trigger popup
  if (status === 401) {
    console.log("error from interceptor: code 401", error);
  } else if (status === 403 || error.errorCode === "KB403001") {
    triggerEvent("DEACTIVATE_USER", { detail: { status, error } });
  } else if (status === 404) {
    triggerEvent("API_INVALID_REQUEST", { detail: { status, error } });
  } else if (status === 500) {
    triggerEvent("API_INTERNAL_SERVER_ERROR", { detail: { status, error } });
  } else if (status === 505) {
    triggerEvent("API_TIMEOUT", { detail: { status, error } });
  }
  return Promise.reject(errorObj);
};

api.interceptors.response.use((response) => response, apiErrorHandler);

// PUBLIC API
export const publicApi = axios.create({
  timeout: 30000,
  transformResponse: [...axios.defaults.transformResponse, (data) => data],
  transformRequest: [(data) => data, ...axios.defaults.transformRequest],
  headers: Auth.forPublicApisHeader(),
});

publicApi.interceptors.request.use(
  (request) => request,
  (error) => Promise.reject(error),
);

publicApi.interceptors.response.use((response) => response, apiErrorHandler);

export const postRequestWithoutAuthorization = (url, data) => {
  return axios({
    method: "post",
    url: `${url}`,
    data: JSON.stringify(data),
    headers: Auth.forPublicApisHeader(),
  });
};

export const postRequestForKYC = (url, data) => {
  return axios({
    method: "post",
    url: `${url}`,
    data: data,
    headers: {
      "Content-Type": "application/form-data",
      Accept: "application/json",
    },
  });
};

export default api;
